import React, { useState} from 'react'
import _ from 'lodash'
import {PricingListTableHeaders} from "./PricingListTableHeaders";
import {PricingListRow} from "./PricingListRow";

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandCircleDownSharpIcon from '@mui/icons-material/ExpandCircleDownSharp';

import './PricingListPage.scss'

import {headerHeight} from "./PricingListPage";
import {usePricingsStore} from "../../stores/pricingsStore";
import {useParams} from "react-router";
import {useUserContext} from "../../contexts/UserContext";

const TOTAL_COLUMNS = 10

const TableCells = ( { times, style, className } ) => {
    return _.times(times, (n) =>
        <td
            style={style}
            key={n}
            className={className}
        />
    )
}

export const PricingListPricing = ( { pricing, trim, style_description} ) => {
    return (
        <tr>
            <TableCells times={TOTAL_COLUMNS - 8}/>
            <PricingListRow
                pricing={pricing}
                trim={trim}
                style_description={style_description}
            />
        </tr>
    )
}

const PricingListTrim = ( { year, trim, collapsed, style_description } ) => {
    const renderPricings = () => _.map(trim.pricings, (pricing) => {
        return (
            <PricingListPricing
                key={pricing.id}
                pricing={pricing}
                trim={trim}
                year={year}
            />
        )
    })

    if (collapsed) return null;

    return (
        <React.Fragment>
            <tr>
                <TableCells times={TOTAL_COLUMNS - 8}/>
                <td className={'trim-sd-name'}>
                    <div className={'trim-name'}>{trim.name}</div>
                </td>
                <TableCells times={TOTAL_COLUMNS - 2}/>
            </tr>
            { renderPricings() }
        </React.Fragment>
    )
}

const PricingListModel = ( { year, modelName, trims, collapsed, shouldExpand } ) => {
    const search = usePricingsStore((state) => state.search)
    const [modelCollpased, setModelCollapsed] = useState(false)

    const renderTrims = () => {
        return (
            _.map(trims, (trim) => {
                return (
                    <PricingListTrim
                        key={trim.id}
                        trim={trim}
                        collapsed={modelCollpased && !Boolean(search.length)}
                        year={year}
                    />
                )
            })
        )
    }
    if (collapsed) return null;

    return (
        <React.Fragment>
            <tr onClick={() => setModelCollapsed(!modelCollpased)} style={{cursor: 'pointer'}}>
                <TableCells times={TOTAL_COLUMNS - 9}/>
                    <td className={'PricingListTable-model_name'}>
                        <div className={'PricingListTable-model_name-button'}>
                            {
                                modelCollpased ?
                                    <ExpandMoreIcon className={'ExpandMoreIcon'} /> :
                                    <ExpandCircleDownSharpIcon className={'ExpandCircleUpSharpIcon'} />
                            }
                            {modelName}
                        </div>
                    </td>
                <TableCells times={TOTAL_COLUMNS - 2}/>
            </tr>
            { renderTrims() }
        </React.Fragment>
    )
}

const PricingListYear = ( { year, models, hasPricing, isOpen, onToggle } ) => {
    const { profileState } = useUserContext()
    const search = usePricingsStore((state) => state.search)
    const shouldExpand = Boolean(hasPricing && profileState?.is_pricing_expanded);
    const yearCollpased = !isOpen
    const setYearCollapsed = () => onToggle()

    const hasPricings = (trims) => {
        if (Array.isArray(trims)) {
            return trims.some(trim => trim.pricings.length)
        }
        return false
    }

    const renderModels = () => {
        return (
            _.map(models, (trims, model_name) => {
                        return <PricingListModel
                            key={model_name}
                            year={year}
                            shouldExpand={hasPricings(trims) && profileState?.is_pricing_expanded}
                            modelName={model_name}
                            trims={trims}
                            collapsed={yearCollpased && !Boolean(search.length)}
                        />
                    }
                )
        )
    }

    return (
        <React.Fragment>
            <tr onClick={() => setYearCollapsed(!yearCollpased)} style={{cursor: 'pointer'}}>
                <td
                    style={{top: `${headerHeight}px`}}
                    className={'sticky-year-td'}
                >
                    <div className={'PricingListTable-year_name-button'}>
                        {
                            yearCollpased ?
                                <ExpandMoreIcon className={'ExpandMoreIcon'} /> :
                                <ExpandCircleDownSharpIcon className={'ExpandCircleUpSharpIcon'} />
                        }
                        {year}
                        <span className="year-state-badge">{yearCollpased ? 'COLLAPSED' : 'EXPANDED'}</span>
                    </div>
                </td>
                <TableCells
                    style={{top: `${headerHeight}px`}}
                    times={TOTAL_COLUMNS - 1}
                    className={'sticky-year-td'}
                />
            </tr>
            { renderModels() }
        </React.Fragment>
    )
}

export const PricingListTable = ( { pricingData, headerHeight } ) => {
    const params = useParams();
    const make = params.make
    // Only one year section may be expanded at a time. Default
    // to the most recent year (highest numeric key).
    const yearKeys = Object.keys(pricingData || {})
    const latestYear = yearKeys.length
        ? yearKeys.reduce((a, b) => (Number(a) > Number(b) ? a : b))
        : null
    // userOpenYear === undefined means the user has not picked one
    // yet, so we fall back to the latest year. After the user
    // clicks any year, userOpenYear becomes a string (the year) or
    // null (to mean "all collapsed"). This way the default updates
    // automatically as `pricingData` loads in async.
    const [userOpenYear, setOpenYear] = useState(undefined)
    const openYear = userOpenYear === undefined ? latestYear : userOpenYear

    const renderYears = () => {
        return (
            _.map(pricingData, (models, year) => {
                const hasPricings = models.__has_pricings
                delete models.__has_pricings
                return (
                    <PricingListYear
                        key={`${year}-${make}`}
                        year={year}
                        models={models}
                        hasPricing={hasPricings}
                        isOpen={String(openYear) === String(year)}
                        onToggle={() => setOpenYear(prev =>
                            String(prev) === String(year) ? null : year)}
                    />
                )
            })
        )
    }

    return (
        <React.Fragment>
            <table>
                <PricingListTableHeaders headerHeight={headerHeight} />
                <tbody>
                    {
                        renderYears()
                    }
                </tbody>
            </table>
        </React.Fragment>
    )
}